/**
 * La présence sur chantier, adossée à Supabase.
 *
 * Le compagnon passe par trois fonctions : `my_attendance` pour lire (il ne lit
 * pas `projects`, et doit pourtant voir le nom du chantier), `pointer_arrivee`
 * et `pointer_depart` pour écrire, à l'heure du serveur. Le bureau lit la table.
 */
import { DomainError } from "@dker/core";
import type { Attendance, AttendanceRepository, ExecutionContext } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

/**
 * Les refus des fonctions sont écrits pour le compagnon (« Vous n'êtes pas
 * affecté à ce chantier. ») : on les rend tels quels plutôt que de les
 * enfouir derrière « Opération refusée par la base ».
 */
function fail(error: { message: string; code?: string }): never {
  const lisible = error.code === "P0001";
  throw new DomainError(
    lisible ? "invalid_input" : "conflict",
    lisible ? error.message : `Opération refusée par la base : ${error.message}`,
    { pgCode: error.code },
  );
}

function toAttendance(row: Row): Attendance {
  const project = Array.isArray(row.projects) ? row.projects[0] : row.projects;
  return {
    id: row.id,
    employeeId: row.employee_id,
    projectId: row.project_id,
    projectName: row.project_name ?? project?.name ?? null,
    workDate: row.work_date,
    arrivedAt: row.arrived_at,
    leftAt: row.left_at ?? null,
  };
}

export function createSupabaseAttendanceRepository(
  clientFor: DbClientFactory,
): AttendanceRepository {
  return {
    async mine(ctx: ExecutionContext) {
      const { data, error } = await clientFor(ctx).rpc("my_attendance");
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toAttendance);
    },

    async clockIn(ctx: ExecutionContext, projectId: string) {
      const { error } = await clientFor(ctx).rpc("pointer_arrivee", { p_project_id: projectId });
      if (error) fail(error);
    },

    async clockOut(ctx: ExecutionContext) {
      const { error } = await clientFor(ctx).rpc("pointer_depart");
      if (error) fail(error);
    },

    async list(ctx: ExecutionContext, from: string, to: string) {
      const { data, error } = await clientFor(ctx)
        .from("site_attendance")
        .select("id, employee_id, project_id, work_date, arrived_at, left_at, projects(name)")
        .gte("work_date", from)
        .lte("work_date", to)
        .order("arrived_at", { ascending: true });
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toAttendance);
    },
  };
}
